import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Switch,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  useMediaQuery,
  useTheme,
  Fade,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ElectricBolt as ElectricIcon,
  AccessTime as TimeIcon,
  Battery20 as BatteryIcon,
  ConnectWithoutContact as ConnectIcon,
  MonetizationOn as MoneyIcon
} from '@mui/icons-material';

const TariffList = ({ tariffs, handleEdit, handleDelete, handleToggleActive }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const formatCurrency = (amount) => {
    return `$${Number(amount).toFixed(2)}`;
  };

  // Componente TariffCard para móviles
  const TariffCard = ({ tariff, index }) => (
    <Fade in={true} timeout={300 + index * 100}>
      <Card
        sx={{
          borderRadius: 3,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: 2,
          border: tariff.is_active ? '2px solid #4CAF50' : '1px solid #E0E0E0',
          backgroundColor: tariff.is_active ? 'rgba(76, 175, 80, 0.02)' : '#FFFFFF',
          '&:hover': {
            transform: { xs: 'none', sm: 'translateY(-2px)' },
            boxShadow: { xs: 3, sm: 6 }
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          {/* Header con nombre y estado */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 2
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: '#002642',
                  fontSize: { xs: '1.1rem', sm: '1.25rem' },
                  mb: 0.5
                }}
              >
                {tariff.name}
              </Typography>
              <Chip
                label={tariff.is_active ? "ACTIVA" : "INACTIVA"}
                size="small"
                sx={{
                  backgroundColor: tariff.is_active ? '#4CAF50' : '#BDBDBD',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  fontSize: '0.75rem'
                }}
              />
            </Box>
            
            {/* Acciones */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {handleEdit && (
                <IconButton
                  onClick={() => handleEdit(tariff)}
                  sx={{
                    color: '#002642',
                    backgroundColor: 'rgba(0, 38, 66, 0.1)',
                    width: 36,
                    height: 36,
                    '&:hover': {
                      backgroundColor: '#002642',
                      color: '#FFFFFF'
                    }
                  }}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {handleToggleActive && (
                <Switch
                  checked={tariff.is_active}
                  onChange={() => handleToggleActive(tariff)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4CAF50'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#4CAF50'
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Grid de precios */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <TimeIcon sx={{ color: '#DB9600', fontSize: '1.25rem', mr: 1 }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#75787B', fontSize: '0.75rem', display: 'block' }}
                  >
                    Por Hora
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ 
                      fontWeight: 600, 
                      color: '#002642',
                      fontSize: '0.9rem'
                    }}
                  >
                    {formatCurrency(tariff.price_per_hour)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <ElectricIcon sx={{ color: '#DB9600', fontSize: '1.25rem', mr: 1 }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#75787B', fontSize: '0.75rem', display: 'block' }}
                  >
                    Por kWh
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ 
                      fontWeight: 600, 
                      color: '#002642',
                      fontSize: '0.9rem'
                    }}
                  >
                    {formatCurrency(tariff.price_per_kwh)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <ConnectIcon sx={{ color: '#DB9600', fontSize: '1.25rem', mr: 1 }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#75787B', fontSize: '0.75rem', display: 'block' }}
                  >
                    Conexión
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ 
                      fontWeight: 600, 
                      color: '#002642',
                      fontSize: '0.9rem'
                    }}
                  >
                    {formatCurrency(tariff.connection_fee)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <MoneyIcon sx={{ color: '#DB9600', fontSize: '1.25rem', mr: 1 }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: '#75787B', fontSize: '0.75rem', display: 'block' }}
                  >
                    Mínimo
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ 
                      fontWeight: 600, 
                      color: '#002642',
                      fontSize: '0.9rem'
                    }}
                  >
                    {formatCurrency(tariff.min_charge)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Fade>
  );

  // Estado vacío
  if (!tariffs || tariffs.length === 0) {
    return (
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
            <ElectricIcon 
              sx={{ 
                fontSize: { xs: '3rem', sm: '4rem' }, 
                color: '#E0E0E0', 
                mb: 2 
              }} 
            />
            <Typography
              variant="h6"
              sx={{
                color: '#75787B',
                mb: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' }
              }}
            >
              No hay tarifas configuradas
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#A0A0A0',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Crea tu primera tarifa energética para comenzar
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Vista móvil con cards
  if (isMobile) {
    return (
      <Grid container spacing={3}>
        {tariffs.map((tariff, index) => (
          <Grid item xs={12} key={tariff.tariff_id}>
            <TariffCard tariff={tariff} index={index} />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Vista desktop con tabla
  return (
    <Card sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
      <TableContainer>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
              <TableCell sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                Nombre
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                Precio por Hora
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                Precio por kWh
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                Cargo por Conexión
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                Cargo Mínimo
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                Estado
              </TableCell>
              {handleEdit && (
                <TableCell align="center" sx={{ fontWeight: 700, color: '#002642', fontSize: '0.9rem' }}>
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tariffs.map((tariff, index) => (
              <Fade in={true} timeout={300 + index * 100} key={tariff.tariff_id}>
                <TableRow
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: 'rgba(0, 38, 66, 0.02)' },
                    backgroundColor: tariff.is_active ? 'rgba(76, 175, 80, 0.02)' : 'inherit'
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: '#002642',
                          fontSize: '0.95rem'
                        }}
                      >
                        {tariff.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.9rem', color: '#75787B' }}>
                    {formatCurrency(tariff.price_per_hour)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.9rem', color: '#75787B' }}>
                    {formatCurrency(tariff.price_per_kwh)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.9rem', color: '#75787B' }}>
                    {formatCurrency(tariff.connection_fee)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.9rem', color: '#75787B' }}>
                    {formatCurrency(tariff.min_charge)}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Chip
                        label={tariff.is_active ? "ACTIVA" : "INACTIVA"}
                        size="small"
                        sx={{
                          backgroundColor: tariff.is_active ? '#4CAF50' : '#BDBDBD',
                          color: '#FFFFFF',
                          fontWeight: 600,
                          fontSize: '0.75rem'
                        }}
                      />
                      {handleToggleActive && (
                        <Switch
                          checked={tariff.is_active}
                          onChange={() => handleToggleActive(tariff)}
                          size="small"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#4CAF50'
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#4CAF50'
                            }
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  {handleEdit && (
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEdit(tariff)}
                        sx={{
                          color: '#002642',
                          backgroundColor: 'rgba(0, 38, 66, 0.1)',
                          '&:hover': {
                            backgroundColor: '#002642',
                            color: '#FFFFFF',
                            transform: 'scale(1.1)'
                          }
                        }}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              </Fade>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default TariffList;
